// Drop-in replacement for react-leaflet's <TileLayer> that transparently
// caches tiles to IndexedDB as the user browses, and serves them offline.
//
// Tiles are loaded via <img> (never fetch()) so we stay compliant with tile
// usage policies like OpenStreetMap's, which blocks programmatic/bulk fetching.
// Caching is done post-load via a <canvas> for servers that send CORS headers
// (Esri, NOAA, OpenSeaMap). Servers that reject cross-origin loads (e.g. OSM
// standard) fall back to a plain <img> load - they display fine but aren't
// cached, which is exactly what their policy requires.
import L from 'leaflet';
import { createTileLayerComponent, updateGridLayer, withPane } from '@react-leaflet/core';
import { getTile, putTile } from '@/lib/tileCache';

const CachedTL = L.TileLayer.extend({
  createTile(coords, done) {
    const img = document.createElement('img');
    img.alt = '';
    img.setAttribute('role', 'presentation');

    const url = this.getTileUrl(coords);
    const cacheId = this.options.cacheId || this._url || 'default';
    const key = `${cacheId}|${coords.z}|${coords.x}|${coords.y}`;
    const self = this;

    let fromCache = false;

    const finish = (err) => done(err || null, img);

    const cacheViaCanvas = () => {
      if (fromCache || self._corsBlocked || img.crossOrigin !== 'anonymous') return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 256;
        canvas.height = img.naturalHeight || 256;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => { if (blob) putTile(key, blob); }, 'image/png');
      } catch (e) {
        /* tainted canvas or unsupported - skip caching */
      }
    };

    const onLoad = () => {
      cacheViaCanvas();
      if (img.src && img.src.startsWith('blob:')) {
        try { URL.revokeObjectURL(img.src); } catch (e) { /* noop */ }
      }
      finish();
    };

    const onError = () => {
      // A cross-origin load was rejected (e.g. OSM). Retry once as a plain
      // <img> with no CORS - displays fine, just won't be cached.
      if (!fromCache && img.crossOrigin === 'anonymous') {
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          self._corsBlocked = true;
        }
        img.removeAttribute('crossorigin');
        img.crossOrigin = '';
        img.src = url;
        return;
      }
      finish(new Error('Tile load error'));
    };

    L.DomEvent.on(img, 'load', onLoad);
    L.DomEvent.on(img, 'error', onError);

    getTile(key)
      .then((blob) => {
        if (blob) {
          fromCache = true;
          img.src = URL.createObjectURL(blob);
          return;
        }
        if (!self._corsBlocked) img.crossOrigin = 'anonymous';
        img.src = url;
      })
      .catch(() => {
        if (!self._corsBlocked) img.crossOrigin = 'anonymous';
        img.src = url;
      });

    return img;
  },
});

export const CachedTileLayer = createTileLayerComponent(
  function createInstance(props, context) {
    const { url, ...options } = props;
    const layer = new CachedTL(url, withPane(options, context));
    return { instance: layer, context };
  },
  function updateInstance(layer, props, prevProps) {
    updateGridLayer(layer, props, prevProps);
    if (props.url != null && props.url !== prevProps.url) {
      layer.setUrl(props.url);
    }
  }
);
